import { Navigate, Route } from "react-router-dom";
import { WarehousePage } from "../pages/WarehousePage";
import { ModelTab } from "../pages/warehouse/ModelTab";
import { MapTab } from "../pages/warehouse/MapTab";
import { MapCaptureTab } from "../pages/warehouse/MapCaptureTab";
import { MapAlignTab } from "../pages/warehouse/MapAlignTab";
import { StructureTab } from "../pages/warehouse/StructureTab";
import { AddressesSection } from "../pages/warehouse/structure/AddressesSection";
import { ZonesSection } from "../pages/warehouse/structure/ZonesSection";
import { HeightsSection } from "../pages/warehouse/structure/HeightsSection";
import { GraphSection } from "../pages/warehouse/structure/GraphSection";
import { useWarehouseId } from "../hooks/useWarehouseId";

/** The pre-ADR-145 single-warehouse URLs resolve against the active warehouse,
 *  or fall back to the list when the user has not picked one. */
export function ActiveWarehouseRedirect({ tab }: { tab: string }) {
  const warehouseId = useWarehouseId();
  return <Navigate replace to={warehouseId ? `/admin/warehouses/${warehouseId}/${tab}` : "/admin/warehouses"} />;
}

/**
 * The warehouse routes as a VALUE, mounted by `App` and by the redirect tests.
 *
 * `App.tsx` is imported only by `main.tsx`, so a test that rebuilds this nesting
 * by hand asserts its own replica: breaking `<Navigate to="../map/align"/>` here
 * used to leave every test green. Exporting the definition is what lets a test
 * fail for the right reason.
 *
 * A module of its own rather than a named export from `App.tsx` — importing App
 * would drag every page in the application into the test's module graph for the
 * sake of ten `<Route>` elements.
 */
export const warehouseRoutes = (
  <>
    {/* Legacy single-warehouse calibration entry — resolves to the active warehouse. */}
    <Route path="/admin/calibration" element={<ActiveWarehouseRedirect tab="model" />} />

    {/* One page per warehouse; tabs are routes, so the flat URLs below
        redirect onto them instead of breaking (spec 2026-08-11). */}
    <Route path="/admin/warehouses/:warehouseId" element={<WarehousePage />}>
      <Route index element={<Navigate to="model" replace />} />
      <Route path="model" element={<ModelTab />} />
      <Route path="map" element={<MapTab />}>
        <Route index element={<Navigate to="capture" replace />} />
        <Route path="capture" element={<MapCaptureTab />} />
        <Route path="align" element={<MapAlignTab />} />
      </Route>
      <Route path="structure" element={<StructureTab />}>
        <Route index element={<Navigate to="addresses" replace />} />
        <Route path="addresses" element={<AddressesSection />} />
        <Route path="zones" element={<ZonesSection />} />
        <Route path="heights" element={<HeightsSection />} />
        <Route path="graph" element={<GraphSection />} />
      </Route>
      <Route path="calibration" element={<Navigate to="../model" replace />} />
      <Route path="mapping" element={<Navigate to="../map/capture" replace />} />
      <Route path="map-calibration" element={<Navigate to="../map/align" replace />} />
    </Route>
  </>
);
